import React, { useEffect, useState } from "react";
import { api, fmtDate, formatErr, API, resolveImage } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Send, Plus, Trash2, Users, Tag, Sparkles, MessageSquare, Smartphone, CheckCircle, TrendingUp, AlertTriangle, Image as ImageIcon, Video, Upload } from "lucide-react";
import { toast } from "sonner";

const FESTIVAL_PRESETS = [
  {
    name: "✏️ Custom (Write Your Own Message)",
    title: "",
    template: "",
  },
  {
    name: "🪔 Diwali Festival Wish",
    title: "Happy Diwali Wishes & Special Dairy Offer",
    template: "🪔 *Happy Diwali to {customer_name}!* ✨\nMay this festival of lights bring prosperity to {business_name}!\n\nCelebrate Diwali with fresh Omkar Paneer, Pure Ghee, and Fresh Milk. Contact us for bulk festive orders!\n\n*Omkar Milk Distributor*",
  },
  {
    name: "🌸 Ganesh Chaturthi Wish",
    title: "Happy Ganesh Chaturthi Greetings",
    template: "🌸 *Happy Ganesh Chaturthi to {customer_name}!* 🐘\nMay Lord Ganesha bless {business_name} with health & success!\n\nSpecial offers available on fresh milk & curd for festival prasad.\n\n*Omkar Milk Distributor*",
  },
  {
    name: "🎉 New Year Wish",
    title: "Happy New Year Greetings",
    template: "🎉 *Happy New Year to {customer_name}!* 🥂\nThank you for partnering with Omkar Milk Distributor in 2026!\n\nWe look forward to supplying fresh dairy products to {business_name} every morning.\n\n*Omkar Milk Distributor*",
  },
  {
    name: "🥛 Daily Fresh Milk Promotion",
    title: "Daily Dairy Order Reminder",
    template: "Hello {customer_name}! 🥛 Friendly reminder from Omkar Milk Distributor. Fresh milk, paneer, and curd are ready for delivery to {business_name}.",
  },
];

export default function MarketingPage() {
  const [activeTab, setActiveTab] = useState("campaigns");
  const [campaigns, setCampaigns] = useState([]);
  const [offers, setOffers] = useState([]);
  const [audience, setAudience] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("All Customers");
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Create Campaign Modal
  const [createCampaignOpen, setCreateCampaignOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    title: "",
    channel: "WhatsApp",
    target_segment: "All Customers",
    message_template: "Hello {customer_name}! 🥛 Omkar Milk Distributor brings you fresh dairy products directly to your doorstep. Contact us to adjust your daily order!",
    media_url: "",
  });

  // Create Offer Modal
  const [createOfferOpen, setCreateOfferOpen] = useState(false);
  const [offerForm, setOfferForm] = useState({
    title: "",
    discount_type: "Flat Discount",
    discount_value: "",
    applicable_product: "All Products",
    min_order_qty: "0",
    valid_from: new Date().toISOString().slice(0, 10),
    valid_to: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    status: true,
  });

  function load() {
    api.get("/marketing/campaigns").then(r => setCampaigns(r.data)).catch(() => {});
    api.get("/marketing/offers").then(r => setOffers(r.data)).catch(() => {});
    fetchAudience(selectedSegment);
  }

  function fetchAudience(segment) {
    api.get("/marketing/audience", { params: { segment } })
      .then(r => setAudience(r.data))
      .catch(() => {});
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    fetchAudience(selectedSegment);
  }, [selectedSegment]);

  async function handleCreateCampaign() {
    if (!campaignForm.title.trim()) return toast.error("Enter campaign title");
    if (!campaignForm.message_template.trim()) return toast.error("Enter message template");

    try {
      const res = await api.post("/marketing/campaigns", campaignForm);
      toast.success("Campaign created! Opening audience list to broadcast WhatsApp messages...");
      setCreateCampaignOpen(false);
      const createdCampaign = res.data;
      setCampaignForm({
        title: "",
        channel: "WhatsApp",
        target_segment: "All Customers",
        message_template: "Hello {customer_name}! 🥛 Omkar Milk Distributor brings you fresh dairy products directly to your doorstep. Contact us to adjust your daily order!",
        media_url: "",
      });
      load();
      setActiveTab("audience");
      setSelectedSegment(createdCampaign.target_segment || "All Customers");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to create campaign");
    }
  }

  async function handleDeleteCampaign(id) {
    if (!window.confirm("Delete this campaign history?")) return;
    await api.delete(`/marketing/campaigns/${id}`);
    toast.success("Campaign removed");
    load();
  }

  async function handleCreateOffer() {
    if (!offerForm.title.trim()) return toast.error("Enter offer title");
    if (!offerForm.discount_value) return toast.error("Enter discount value");

    try {
      await api.post("/marketing/offers", {
        ...offerForm,
        discount_value: parseFloat(offerForm.discount_value),
        min_order_qty: parseFloat(offerForm.min_order_qty || 0),
      });
      toast.success("Promotional offer created!");
      setCreateOfferOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to create offer");
    }
  }

  async function handleDeleteOffer(id) {
    if (!window.confirm("Delete this promotional offer?")) return;
    await api.delete(`/marketing/offers/${id}`);
    toast.success("Offer deleted");
    load();
  }

  async function handleMediaFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/marketing/upload-media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCampaignForm(prev => ({ ...prev, media_url: res.data.url }));
      toast.success("Festival Image/Video uploaded successfully!");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to upload media");
    } finally {
      setUploadingMedia(false);
    }
  }

  function launchWhatsAppBroadcast(c, customer) {
    let rawMsg = c.message_template
      .replace(/{customer_name}/g, customer.customer_name || customer.business_name)
      .replace(/{business_name}/g, customer.business_name)
      .replace(/{outstanding_dues}/g, `₹${customer.outstanding || 0}`);

    if (c.media_url) {
      const fullMediaUrl = window.location.origin + c.media_url;
      rawMsg += `\n\n🖼️ *Festival Greeting Image/Video:* ${fullMediaUrl}`;
    }

    const phoneRaw = (customer.mobile || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(rawMsg);
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  return (
    <div data-testid="marketing-page">
      <PageHeader
        eyebrow="Growth & Engagement"
        title="Marketing & Campaign Center"
        description="Launch WhatsApp broadcast campaigns, target customer segments, and manage promotional rate discounts."
        actions={
          <div className="flex gap-2">
            <Button
              onClick={() => setCreateOfferOpen(true)}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Tag size={16} className="mr-1.5" />
              New Offer
            </Button>
            <Button
              onClick={() => setCreateCampaignOpen(true)}
              className="bg-cobalt-500 hover:bg-cobalt-700 text-white"
            >
              <Megaphone size={16} className="mr-1.5" />
              Launch Campaign
            </Button>
          </div>
        }
      />

      {/* Overview Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card padding="p-4" className="border-l-4 border-l-cobalt-600">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Total Campaigns</div>
          <div className="font-heading font-mono text-2xl font-bold mt-1 text-slate-900">{campaigns.length}</div>
          <div className="text-xs text-slate-500 mt-1">Broadcast messages sent</div>
        </Card>
        <Card padding="p-4" className="border-l-4 border-l-emerald-500">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Active Offers</div>
          <div className="font-heading font-mono text-2xl font-bold mt-1 text-emerald-600">{offers.filter(o => o.status).length}</div>
          <div className="text-xs text-slate-500 mt-1">Promotions active</div>
        </Card>
        <Card padding="p-4" className="border-l-4 border-l-indigo-500">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Audience Reach</div>
          <div className="font-heading font-mono text-2xl font-bold mt-1 text-indigo-600">{audience.length}</div>
          <div className="text-xs text-slate-500 mt-1">Customers in segment ({selectedSegment})</div>
        </Card>
        <Card padding="p-4" className="border-l-4 border-l-amber-500">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Channel Focus</div>
          <div className="font-heading font-semibold text-lg mt-1 text-amber-700 flex items-center gap-1.5">
            <Smartphone size={18} />
            WhatsApp Direct
          </div>
          <div className="text-xs text-slate-500 mt-1">High conversion reach</div>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 flex-wrap h-auto mb-4">
          <TabsTrigger value="campaigns">Broadcast Campaigns ({campaigns.length})</TabsTrigger>
          <TabsTrigger value="audience">Audience Segmentation ({audience.length})</TabsTrigger>
          <TabsTrigger value="offers">Promotional Offers ({offers.length})</TabsTrigger>
        </TabsList>

        {/* TAB 1: Campaigns */}
        <TabsContent value="campaigns">
          <Card padding="p-0">
            {campaigns.length === 0 ? (
              <div className="p-8 text-center">
                <Empty title="No marketing campaigns launched yet" description="Click 'Launch Campaign' to create your first broadcast message." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="ddms-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Channel</th>
                      <th>Media Attachment</th>
                      <th>Target Segment</th>
                      <th>Message Preview</th>
                      <th className="num">Recipients</th>
                      <th>Date</th>
                      <th className="num">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaigns.map(c => (
                      <tr key={c.id}>
                        <td className="font-semibold text-slate-900">{c.title}</td>
                        <td>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {c.channel}
                          </span>
                        </td>
                        <td>
                          {c.media_url ? (
                            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1 w-fit">
                              <ImageIcon size={12} /> Media Attached
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">Text Only</span>
                          )}
                        </td>
                        <td><span className="font-medium text-cobalt-700">{c.target_segment}</span></td>
                        <td className="max-w-xs truncate text-xs text-slate-600">{c.message_template}</td>
                        <td className="num font-mono font-semibold">{c.recipient_count}</td>
                        <td className="text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                        <td className="num">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setActiveTab("audience");
                                setSelectedSegment(c.target_segment);
                                toast.info(`Opened audience for ${c.target_segment}. Click 'Send Message' next to any customer!`);
                              }}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 bg-emerald-50/40 text-xs gap-1"
                              title="Send to WhatsApp Audience"
                            >
                              <Send size={12} />
                              Send WhatsApp
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteCampaign(c.id)}
                              className="text-rose-600 border-rose-200 hover:bg-rose-50"
                              title="Delete Campaign"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 2: Audience */}
        <TabsContent value="audience">
          <Card padding="p-4" className="mb-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-1">Select Customer Segment</label>
                <Select value={selectedSegment} onValueChange={setSelectedSegment}>
                  <SelectTrigger className="w-64 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All Customers">All Active Customers</SelectItem>
                    <SelectItem value="Inactive (Last 7 Days)">Inactive (No Orders in 7 Days)</SelectItem>
                    <SelectItem value="Dues Pending (>₹1000)">Dues Pending (&gt; ₹1,000)</SelectItem>
                    <SelectItem value="High Volume Buyers">High Volume / Advance Account</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-slate-500">
                Found <strong className="text-slate-900 font-semibold">{audience.length}</strong> matching customers in this segment.
              </div>
            </div>
          </Card>

          <Card padding="p-0">
            {audience.length === 0 ? (
              <div className="p-8"><Empty title="No customers found in this segment" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="ddms-table">
                  <thead>
                    <tr>
                      <th>Business Name</th>
                      <th>Contact Person</th>
                      <th>Mobile</th>
                      <th className="num">Outstanding Dues</th>
                      <th className="num">Advance Balance</th>
                      <th className="num">Quick WhatsApp Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audience.map(cust => (
                      <tr key={cust.id}>
                        <td className="font-semibold text-slate-900">{cust.business_name}</td>
                        <td>{cust.customer_name}</td>
                        <td className="font-mono text-xs">{cust.mobile}</td>
                        <td className="num font-mono text-rose-600 font-semibold">₹{(cust.outstanding || 0).toFixed(2)}</td>
                        <td className="num font-mono text-emerald-600 font-semibold">₹{(cust.advance || 0).toFixed(2)}</td>
                        <td className="num">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const template = campaigns[0] || {
                                message_template: `Hello ${cust.customer_name}! 🥛 Friendly greetings from Omkar Milk Distributor. We look forward to serving your dairy needs today!`
                              };
                              launchWhatsAppBroadcast(template, cust);
                            }}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 bg-emerald-50/40"
                          >
                            <Send size={12} className="mr-1" />
                            Send Message
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* TAB 3: Offers */}
        <TabsContent value="offers">
          <Card padding="p-0">
            {offers.length === 0 ? (
              <div className="p-8 text-center">
                <Empty title="No promotional offers created yet" description="Click 'New Offer' to create festive rate discounts or volume bonuses." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="ddms-table">
                  <thead>
                    <tr>
                      <th>Offer Title</th>
                      <th>Discount Type</th>
                      <th className="num">Discount Value</th>
                      <th>Applicable Product</th>
                      <th>Validity</th>
                      <th>Status</th>
                      <th className="num">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offers.map(o => (
                      <tr key={o.id}>
                        <td className="font-semibold text-slate-900">{o.title}</td>
                        <td><span className="font-medium text-indigo-700">{o.discount_type}</span></td>
                        <td className="num font-mono font-bold text-emerald-600">
                          {o.discount_type === "Percentage" ? `${o.discount_value}%` : `₹${o.discount_value}`}
                        </td>
                        <td>{o.applicable_product}</td>
                        <td className="text-xs text-slate-500">{fmtDate(o.valid_from)} to {fmtDate(o.valid_to)}</td>
                        <td>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${o.status ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {o.status ? "Active" : "Expired"}
                          </span>
                        </td>
                        <td className="num">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteOffer(o.id)}
                            className="text-rose-600 border-rose-200 hover:bg-rose-50"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* CREATE CAMPAIGN DIALOG */}
      <Dialog open={createCampaignOpen} onOpenChange={setCreateCampaignOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 font-heading">
              <Megaphone className="text-cobalt-600" size={22} />
              Launch Broadcast Campaign
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Festival / Greeting Quick Preset</label>
              <Select
                onValueChange={(val) => {
                  const preset = FESTIVAL_PRESETS.find(p => p.name === val);
                  if (preset) {
                    setCampaignForm(prev => ({
                      ...prev,
                      title: preset.title,
                      message_template: preset.template,
                    }));
                    toast.info(`Loaded ${preset.name} template!`);
                  }
                }}
              >
                <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Choose a festival template (Diwali, Ganesh Chaturthi, New Year...)" /></SelectTrigger>
                <SelectContent>
                  {FESTIVAL_PRESETS.map(p => (
                    <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Campaign Title</label>
              <Input
                placeholder="e.g. Diwali Crate Discount & Greetings"
                value={campaignForm.title}
                onChange={e => setCampaignForm({ ...campaignForm, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Channel</label>
                <Select
                  value={campaignForm.channel}
                  onValueChange={v => setCampaignForm({ ...campaignForm, channel: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WhatsApp">WhatsApp Broadcast</SelectItem>
                    <SelectItem value="SMS">SMS Text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Target Segment</label>
                <Select
                  value={campaignForm.target_segment}
                  onValueChange={v => setCampaignForm({ ...campaignForm, target_segment: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All Customers">All Active Customers</SelectItem>
                    <SelectItem value="Inactive (Last 7 Days)">Inactive (Last 7 Days)</SelectItem>
                    <SelectItem value="Dues Pending (>₹1000)">Dues Pending (&gt; ₹1,000)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">
                Attach Festival Banner Image / Greeting Video
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  id="media-upload-input"
                  accept="image/*,video/*,*/*"
                  onChange={(e) => {
                    handleMediaFileUpload(e);
                    e.target.value = "";
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="media-upload-input"
                  className="cursor-pointer border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium px-3.5 py-2 rounded-md shadow-sm inline-flex items-center gap-2 transition-colors select-none"
                >
                  <Upload size={16} />
                  {uploadingMedia ? "Uploading..." : "Upload Image / Video"}
                </label>
                {campaignForm.media_url && (
                  <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <CheckCircle size={14} /> Media Attached
                  </span>
                )}
              </div>
              {campaignForm.media_url && (
                <div className="mt-2 p-2 bg-slate-50 border border-slate-200 rounded-lg max-h-36 overflow-hidden flex items-center justify-between">
                  {campaignForm.media_url.match(/\.(mp4|mov|avi)$/i) ? (
                    <video src={resolveImage(campaignForm.media_url)} className="h-28 rounded object-cover" controls />
                  ) : (
                    <img src={resolveImage(campaignForm.media_url)} alt="Media preview" className="h-28 rounded object-cover" />
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCampaignForm(prev => ({ ...prev, media_url: "" }))}
                    className="text-rose-600 hover:bg-rose-50"
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Message Template</label>
              <Textarea
                rows={5}
                value={campaignForm.message_template}
                onChange={e => setCampaignForm({ ...campaignForm, message_template: e.target.value })}
                placeholder="Use tags: {customer_name}, {business_name}, {outstanding_dues}"
              />
              <span className="text-[11px] text-slate-400 mt-1 block">
                Available tags: <code>{"{customer_name}"}</code>, <code>{"{business_name}"}</code>, <code>{"{outstanding_dues}"}</code>
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateCampaignOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} className="bg-cobalt-600 hover:bg-cobalt-700 text-white">
              Launch Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CREATE OFFER DIALOG */}
      <Dialog open={createOfferOpen} onOpenChange={setCreateOfferOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 font-heading">
              <Tag className="text-emerald-600" size={22} />
              Create Promotional Offer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Offer Title</label>
              <Input
                placeholder="e.g. ₹2/Ltr Discount on Paneer"
                value={offerForm.title}
                onChange={e => setOfferForm({ ...offerForm, title: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Discount Type</label>
                <Select
                  value={offerForm.discount_type}
                  onValueChange={v => setOfferForm({ ...offerForm, discount_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Flat Discount">Flat ₹ Off</SelectItem>
                    <SelectItem value="Percentage">Percentage % Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Discount Value</label>
                <Input
                  type="number"
                  placeholder="e.g. 2 or 5"
                  value={offerForm.discount_value}
                  onChange={e => setOfferForm({ ...offerForm, discount_value: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Valid From</label>
                <Input
                  type="date"
                  value={offerForm.valid_from}
                  onChange={e => setOfferForm({ ...offerForm, valid_from: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Valid To</label>
                <Input
                  type="date"
                  value={offerForm.valid_to}
                  onChange={e => setOfferForm({ ...offerForm, valid_to: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOfferOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateOffer} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Save Offer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
